import React, {useEffect, useState} from 'react';
import {useNavigate, useSearchParams} from 'react-router-dom';
import {Alert, Box, Button, Container, Grid, Paper, Typography} from '@mui/material';
import {useUserData} from '../hooks/useUserData';
import {logError, logWarning, logInfo} from '../utils/logging';
import {sttApi} from '../api/sttApi';

import AccountInfo from './dashboard/AccountInfo';
import PlanDetails from './dashboard/PlanDetails';
import ApiKeysList from './dashboard/ApiKeysList';
import PlanSelectionDialog from './dashboard/PlanSelectionDialog';
import TopUpDialog from './dashboard/TopUpDialog';
import DeleteApiKeyDialog from './dashboard/DeleteApiKeyDialog';
import Notification from './common/Notification';

function Dashboard() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const {
        userData,
        availablePlans,
        topUpPackages,
        error: dataError,
        loading: dataLoading,
        refetchData
    } = useUserData();

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const [planDialogOpen, setPlanDialogOpen] = useState(false);
    const [topUpDialogOpen, setTopUpDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [apiKeyToDelete, setApiKeyToDelete] = useState(null);
    const [showApiKeys, setShowApiKeys] = useState({});

    const [paymentMessage, setPaymentMessage] = useState('');
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success'
    });

    useEffect(() => {
        const success = searchParams.get('success');       // checkout success
        const canceled = searchParams.get('canceled');     // checkout canceled
        const sessionId = searchParams.get('session_id');  // checkout session
        const billingReturn = searchParams.get('billing'); // portal return marker (billing=return)

        const run = async () => {
            // návrat z customer portálu
            if (billingReturn === 'return') {
                setPaymentMessage('Billing updated. Refreshing your account…');
                navigate('/dashboard', {replace: true});
                await refetchData();
                return;
            }

            // návrat z checkoutu (topup nebo první subscription)
            if (success === 'true') {
                try {
                    if (sessionId) {
                        await sttApi.confirmCheckout(sessionId);
                    }
                } catch (e) {
                    logWarning('Checkout confirmation failed (probably webhook already handled it):', {error: e});
                } finally {
                    setPaymentMessage('Payment successful! Your account has been updated.');
                    navigate('/dashboard', {replace: true});
                    await refetchData();
                }
                return;
            }

            if (canceled === 'true') {
                setPaymentMessage('Payment was canceled. No changes were made to your account.');
                navigate('/dashboard', {replace: true});
            }
        };

        run();
    }, [navigate, searchParams, refetchData]);

    useEffect(() => {
        if (!paymentMessage) return;
        const timer = setTimeout(() => setPaymentMessage(''), 5000);
        return () => clearTimeout(timer);
    }, [paymentMessage]);

    const showSnackbar = (message, severity = 'success') => {
        setSnackbar({open: true, message, severity});
    };

    const getRedirectUrl = (data) =>
        data?.redirect_url || data?.portal_url || data?.checkout_url || data?.url || null;

    const handleOpenPortal = async () => {
        setLoading(true);
        setError(null);

        try {
            const returnUrl = `${window.location.origin}/dashboard`;
            const res = await sttApi.createPortalSession(returnUrl);
            const redirectUrl = sttApi.getRedirectUrl(res);
            
            if (redirectUrl) {
                window.location.href = redirectUrl;
            } else {
                throw new Error('No redirect URL received from portal session');
            }
        } catch (err) {
            setError(err);
            showSnackbar(err?.response?.data?.detail || err?.message || 'Failed to open billing portal', 'error');
            setLoading(false);
        }
    };

    const handlePlanUpgrade = async (planId) => {
        setLoading(true);
        setError(null);

        try {
            const selectedPlan = (availablePlans || []).find(p => p.id === planId);
            const planName = selectedPlan?.name || 'selected plan';

            const res = await sttApi.changePlan({
                product_id: planId,
                success_url: `${window.location.origin}/dashboard?success=true&session_id={CHECKOUT_SESSION_ID}`,
                cancel_url: `${window.location.origin}/dashboard?canceled=true`,
                return_url: `${window.location.origin}/dashboard?billing=return`,
            });

            const url = getRedirectUrl(res);
            console.log("changePlan response:", res);
            console.log("redirect url:", url);

            if (url) {
                window.location.assign(url);
                return;
            }
            if (url) {
                window.location.href = url;
                return;
            }


            showSnackbar(`Plan change initiated for ${planName}.`, 'success');
            setPlanDialogOpen(false);
            refetchData();
        } catch (err) {
            setError(err);
            showSnackbar(`Error: ${err?.message || 'Failed to change plan'}`, 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleTopUpPurchase = async (productId) => {
        setLoading(true);
        setError(null);

        try {
            const selectedProduct = (topUpPackages || []).find(p => p.id === productId);
            if (!selectedProduct) {
                showSnackbar('Invalid product selected', 'error');
                return;
            }

            const data = await sttApi.createCheckoutSession({
                product_id: productId,
                success_url: `${window.location.origin}/dashboard?success=true&session_id={CHECKOUT_SESSION_ID}`,
                cancel_url: `${window.location.origin}/dashboard?canceled=true`,
                metadata: {
                    type: 'topup',
                    minutes: selectedProduct.minutes,
                    topup_name: selectedProduct.name,
                },
            });

            const url = sttApi.getRedirectUrl ? sttApi.getRedirectUrl(data) : getRedirectUrl(data);
            if (url) {
                window.location.href = url;
                return;
            }

            showSnackbar('Failed to create checkout session. Please try again.', 'error');
        } catch (err) {
            setError(err);
            showSnackbar(`Payment error: ${err?.message || 'Failed to create checkout session'}`, 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteApiKey = async () => {
        setLoading(true);
        setError(null);
        if (!apiKeyToDelete) return;
        try {
            await sttApi.deactivateApiKey(apiKeyToDelete.id);
            refetchData();
            setDeleteDialogOpen(false);
            setApiKeyToDelete(null);
        } catch (err) {
            setError(err);
            showSnackbar(err?.message || 'Failed to deactivate API key', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleRestoreApiKey = async (apiKeyId) => {
        setLoading(true);
        setError(null);
        try {
            await sttApi.restoreApiKey(apiKeyId);
            refetchData();
        } catch (err) {
            setError(err);
            showSnackbar(err?.message || 'Failed to restore API key', 'error');
        } finally {
            setLoading(false);
        }
    };

    const copyApiKey = async (apiKey) => {
        const textToCopy = apiKey.key || '';
        if (!textToCopy) {
            showSnackbar('Nothing to copy', 'warning');
            return;
        }

        // 1) Modern clipboard API (works in HTTPS / localhost)
        try {
            if (window.isSecureContext && navigator.clipboard?.writeText) {
                await navigator.clipboard.writeText(textToCopy);
                showSnackbar('API key copied to clipboard', 'success');
                return;
            }
        } catch (err) {
            // Continue to fallback
            logInfo('Clipboard API failed, trying fallback', { error: err });
        }

        // 2) Fallback (more compatible)
        try {
            const textArea = document.createElement('textarea');
            textArea.value = textToCopy;

            // Prevent scrolling to bottom on iOS / keep off-screen
            textArea.style.position = 'fixed';
            textArea.style.top = '0';
            textArea.style.left = '0';
            textArea.style.width = '1px';
            textArea.style.height = '1px';
            textArea.style.opacity = '0';

            document.body.appendChild(textArea);
            textArea.focus();
            textArea.select();
            textArea.setSelectionRange(0, textArea.value.length); // important for mobile

            const successful = document.execCommand('copy');
            document.body.removeChild(textArea);

            if (!successful) {
                throw new Error('execCommand(copy) returned false');
            }

            showSnackbar('API key copied to clipboard', 'success');
        } catch (fallbackErr) {
            logError(fallbackErr, {context: 'Dashboard copyApiKey fallback'});
            showSnackbar('Failed to copy API key to clipboard', 'error');
        }
    };

    const toggleApiKeyVisibility = (apiKeyId) => {
        setShowApiKeys(prev => ({
            ...prev,
            [apiKeyId]: !prev[apiKeyId]
        }));
    };

    const handleSnackbarClose = (event, reason) => {
        if (reason === 'clickaway') return;
        setSnackbar(prev => ({...prev, open: false}));
    };

    if (dataLoading || loading) {
        return (
            <Container maxWidth="md">
                <Typography variant="h6" sx={{mt: 4}}>
                    Loading...
                </Typography>
            </Container>
        );
    }

    if (dataError || error) {
        return (
            <Container maxWidth="md">
                <Alert severity="error" sx={{mt: 4}}>
                    {dataError || error?.message}
                </Alert>
            </Container>
        );
    }

    if (!userData) return null;

    return (
        <Container maxWidth="lg">
            <Paper elevation={3} sx={{p: 4, mt: 4}}>
                <Typography variant="h4" component="h1" gutterBottom>
                        Dashboard
                    </Typography>

                {paymentMessage && (
                    <Alert severity="success" sx={{mb: 2}}>
                        {paymentMessage}
                    </Alert>
                )}

                {error && (
                    <Alert severity="error" sx={{mb: 2}}>
                        {error?.message}
                    </Alert>
                )}

                <PlanDetails
                    subscription={userData.subscription}
                    topUps={userData.top_ups}
                    topUpUsage={userData.top_up_usage}
                    onOpenPlanDialog={() => setPlanDialogOpen(true)}
                    onOpenTopUpDialog={() => setTopUpDialogOpen(true)}
                    onOpenPortal={handleOpenPortal}
                />

                {userData.api_keys && (
                    <ApiKeysList
                        apiKeys={userData.api_keys}
                        onDeactivate={(apiKey) => {
                            setApiKeyToDelete(apiKey);
                            setDeleteDialogOpen(true);
                        }}
                        onRestore={handleRestoreApiKey}
                        onShowKey={toggleApiKeyVisibility}
                        onCopyKey={copyApiKey}
                        showApiKeys={showApiKeys}
                        onNavigate={navigate}
                    />
                )}

                <Box sx={{mt: 4, textAlign: 'center'}}>
                    <Button variant="outlined" color="primary" onClick={() => navigate('/jobs')}>
                        Go to Transcription Jobs
                    </Button>
                </Box>
            </Paper>

            <PlanSelectionDialog
                open={planDialogOpen}
                onClose={() => setPlanDialogOpen(false)}
                availablePlans={availablePlans}
                onPlanUpgrade={handlePlanUpgrade}
                loading={loading}
                currentPlanName={userData.subscription?.plan_name}
            />

            <TopUpDialog
                open={topUpDialogOpen}
                onClose={() => setTopUpDialogOpen(false)}
                topUpPackages={topUpPackages}
                onTopUpPurchase={handleTopUpPurchase}
                loading={loading}
            />

            <DeleteApiKeyDialog
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
                onConfirm={handleDeleteApiKey}
                apiKeyToDelete={apiKeyToDelete}
            />

            <Notification
                open={snackbar.open}
                message={snackbar.message}
                severity={snackbar.severity}
                onClose={handleSnackbarClose}
            />
        </Container>
    );
}

export default Dashboard;
